import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useContext, useState } from "react";
import { usuariosBuscar } from "../../api/usuarios.api";
import { DataContext } from "../../../../app/providers/DataContext";
import { obtenerLocalStorage } from "../../../../shared/utils/funciones";
import { useMobileDetection } from "../../../../shared/hooks/useMobileDetection";
import { CONST_KEY_USUARIO_AUTENTICADO, CONST_ROL_MASTER } from "../../../../app/constants/constanst-valores";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useUsuariosAcciones } from "./useUsuariosAcciones";
import { useUsuariosPermisosBotones } from "./useUsuariosPermisosBotones";
import { useUsuariosFiltros } from "./useUsuariosFiltros";

// ítem 15 (React Query, fase 2): referencia ESTABLE para el listado vacío. El useEffect que congela
// `sucursalesTabla` está keyed por [listaUsuarios]; si `listaUsuarios` fuese un `?? []` nuevo en cada
// render (mientras la query aún no trae datos), el efecto dispararía de más. Con una ref estable, solo
// dispara cuando cambian los RESULTADOS reales — que es justo lo que protege la columna "Sucursal".
const EMPTY_USUARIOS: any[] = [];

// Estado y lógica de UsuariosPage. El orden de los hooks es el mismo que tenía
// la página: contexto, estados, ubicación, formulario, React Query, navegación
// (useUsuariosAcciones), permisos de botones, filtros y carga inicial.
export const useUsuarios = () => {

  const { listaRutasDeAcceso } = useContext(DataContext);

  const [listaCompanias, setListaCompanias] = useState<any[]>([])
  const [listaSucursales, setListaSucursales] = useState<any[]>([])
  const [listaSedes, setListaSedes] = useState<any[]>([])
  // Carga de los filtros/selects (compañía, sucursal, sede) al montar. Empieza en true
  // para mostrar "Cargando…" en los selects y que NO parezca que la data falla mientras llega.
  const [isLoadingFiltros, setIsLoadingFiltros] = useState(true)

  const usuarioAutenticado = obtenerLocalStorage(CONST_KEY_USUARIO_AUTENTICADO);

  // console.log('usuarioAutenticado',usuarioAutenticado)
  const rol = usuarioAutenticado?.tipo

  //Estados para ver si mostrar o no los botones
  const [mostrarBotonAgregar, setMostrarBotonAgregar] = useState(false)
  const [mostrarBotonEditar, setMostrarBotonEditar] = useState(false)
  const [mostrarBotonEliminar, setMostrarBotonEliminar] = useState(false)
  const [mostrarBotonUsuarioGrupo, setMostrarBotonUsuarioGrupo] = useState(false)
  const [mostrarBotonUsuarioResetearContraseña, setMostrarBotonUsuarioResetearContraseña] = useState(false)


  //Obtener datos para la busqueda rapida
  const location = useLocation();
  const datosParaBusquedaRapida = location?.state || null

  //console.log("los datos de busqueda son: ", datosParaBusquedaRapida)

  const isMobile = useMobileDetection()

  const { register, handleSubmit, watch, setValue } = useForm<any>({
    defaultValues: {
      NombreCompleto: "",
      Estado: "A",
    }
  });


  // ítem 15 (React Query, fase 2): SOLO el listado de usuarios pasa a useQuery; toda la maquinaria
  // de selects (compañía/sucursal/sede), la cascada onChange, la siembra por location.state y el
  // snapshot de abajo quedan INTACTOS.
  const queryClient = useQueryClient()
  const [filtrosAplicados, setFiltrosAplicados] = useState<any>(null)

  const { data: usuariosData, isFetching } = useQuery({
    queryKey: ["seg-usuarios", filtrosAplicados],
    queryFn: () => usuariosBuscar({ ...filtrosAplicados, token: usuarioAutenticado?.token }),
    enabled: filtrosAplicados !== null,
  })

  // Derivados (equivalentes 1:1 a listaUsuarios/isLoading/buscar). EMPTY_USUARIOS estable para el snapshot.
  const listaUsuarios: any[] = usuariosData?.usuarios ?? EMPTY_USUARIOS
  const isLoading = isFetching
  const buscar = filtrosAplicados !== null

  // Snapshot de las sucursales con las que se ETIQUETA la tabla. Se congela al momento de la
  // busqueda: cambiar el filtro de Compania (que re-carga listaSucursales para el SELECT) ya
  // NO deja en blanco la columna "Sucursal" de los resultados actuales; solo cambia al buscar.
  const [sucursalesTabla, setSucursalesTabla] = useState<any[]>([])

  // Opción "TODOS" del filtro Sucursal (usuarios de todas las sucursales de la compañía): solo MASTER.
  const [mostrarOpcionTodos, setMostrarOpcionTodos] = useState(rol === CONST_ROL_MASTER)

  const onSubmitBuscar = (data) => {
    // Aplica los filtros del formulario; useQuery re-consulta. invalidate = paridad con el flujo
    // previo (siempre re-consultaba al pulsar buscar, aunque no cambien los filtros).
    setFiltrosAplicados({
      Compania: data?.compania,
      Estado: data?.Estado,
      NombreCompleto: data?.NombreCompleto,
      Sede: data?.Sede || "T",
      Sucursal: data?.sucursal,
    })
    queryClient.invalidateQueries({ queryKey: ["seg-usuarios"] })
  };

  const {
    resetPassword,
    usuarioAgregar,
    usuarioGrupo,
    usuarioEditar,
    usuarioEliminar,
    obtenerUsuarios,
  } = useUsuariosAcciones({
    usuarioAutenticado,
    watch,
    queryClient,
    setFiltrosAplicados,
  })

  useUsuariosPermisosBotones({
    listaRutasDeAcceso,
    rol,
    setMostrarBotonAgregar,
    setMostrarBotonEditar,
    setMostrarBotonEliminar,
    setMostrarBotonUsuarioGrupo,
    setMostrarBotonUsuarioResetearContraseña,
  })

  const { obtenerSucursalesPorCompania, obtenerSedesPorSucursal } = useUsuariosFiltros({
    usuarioAutenticado,
    rol,
    datosParaBusquedaRapida,
    watch,
    setValue,
    listaSucursales,
    listaUsuarios,
    setListaCompanias,
    setListaSucursales,
    setListaSedes,
    setIsLoadingFiltros,
    setSucursalesTabla,
    setMostrarOpcionTodos,
    obtenerUsuarios,
  })

  return {
    register,
    watch,
    handleSubmit,
    onSubmitBuscar,
    listaCompanias,
    listaSucursales,
    listaSedes,
    obtenerSucursalesPorCompania,
    obtenerSedesPorSucursal,
    mostrarOpcionTodos,
    isMobile,
    isLoadingFiltros,
    mostrarBotonAgregar,
    usuarioAgregar,
    isLoading,
    listaUsuarios,
    sucursalesTabla,
    buscar,
    mostrarBotonUsuarioGrupo,
    mostrarBotonUsuarioResetearContraseña,
    mostrarBotonEditar,
    mostrarBotonEliminar,
    usuarioGrupo,
    resetPassword,
    usuarioEditar,
    usuarioEliminar,
  }
}
